import { jsx, jsxs } from "react/jsx-runtime";
import { createElement } from "react";
import { debounce, find, get, some, isArray } from "lodash-es";
import { showContextMenu } from "@teselagen/ui";
import {
  Button,
  ButtonGroup,
  Intent,
  Icon,
  Tooltip,
  ContextMenu
} from "@blueprintjs/core";
import PropTypes from "prop-types";
import VersionHistoryView from "../VersionHistoryView";
import { importSequenceFromFile } from "../withEditorProps";
import getAdditionalEnzymesSelector from "../selectors/getAdditionalEnzymesSelector";
import { showAddOrEditAnnotationDialog } from "../GlobalDialogUtils";
import "../Reflex/reflex-styles.css";
import React from "react";
import AlignmentView from "../AlignmentView";
import { compose } from "redux";
import { connectToEditor, handleSave } from "../withEditorProps";
import { withHandlers } from "recompose";
import CommandHotkeyHandler from "./CommandHotkeyHandler";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "../Reflex";
import { flatMap, map, filter, pick, camelCase } from "lodash-es";
import { ToolBar } from "../ToolBar";
import CircularView from "../CircularView";
import LinearView from "../LinearView";
import RowView from "../RowView";
import StatusBar from "../StatusBar";
import DropHandler from "./DropHandler";
import PropertiesDialog from "../helperComponents/PropertiesDialog";
import "./style.css";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import DigestTool from "../DigestTool/DigestTool";
import { insertItem, removeItem } from "../utils/arrayUtils";
import Mismatches from "../AlignmentView/Mismatches";
import SimpleCircularOrLinearView from "../SimpleCircularOrLinearView";
import { userDefinedHandlersAndOpts } from "./userDefinedHandlersAndOpts";
import { GlobalDialog } from "../GlobalDialog";
import isMobile from "is-mobile";
import { getClientX, getClientY } from "../utils/editorUtils";
import PCRTool from "../PCRTool/PCRTool";
import classNames from "classnames";
import { tabHeight } from "../constants";
const _panelMap = {
  circular: {
    comp: CircularView,
    panelSpecificProps: ["extraAnnotationProps"]
  },
  sequence: {
    comp: RowView,
    panelSpecificProps: ["extraAnnotationProps"]
  },
  rail: { comp: LinearView, panelSpecificProps: ["extraAnnotationProps"] },
  // alignmentTool: AlignmentTool,
  alignment: AlignmentView,
  digestTool: DigestTool,
  pcrTool: PCRTool,
  properties: {
    comp: PropertiesDialog,
    panelSpecificProps: ["PropertiesProps"]
  },
  mismatches: Mismatches
};
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};
const getSplitScreenListStyle = (isDraggingOver, isDragging) => {
  return {
    position: "absolute",
    // top: "-20px",
    height: "100%",
    // background: "lightgreen",
    width: "50%",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    wordWrap: "normal",
    opacity: 0,
    ...isDragging && { opacity: 0.7, zIndex: 1e4, background: "lightgrey" },
    ...isDraggingOver && { background: "lightblue" },
    left: "50%"
  };
};
export class Editor extends React.Component {
  state = {
    rotationRadians: 0,
    zoomLevel: 1,
    isHotkeyDialogOpen: false,
    tabDragging: false,
    previewModeFullscreen: false
  };
  getExtraPanel = () => {
    return [];
  };
  getChildContext() {
    return { blueprintPortalClassName: "ove-portal" };
  }
  componentDidUpdate(prevProps) {
    if (this.props.initialAnnotationToEdit && !this.hasShownInitialAnnotationToEditDialog && !this.inPreviewMode) {
      ["part", "feature", "primer"].forEach((type) => {
        if (this.props.initialAnnotationToEdit.startsWith(type)) {
          const annid = this.props.initialAnnotationToEdit.replace(
            type + "-",
            ""
          );
          const anns = this.props.sequenceData[type + "s"];
          const annotation = find(anns, (a) => a.id === annid);
          if (annotation) {
            showAddOrEditAnnotationDialog({ type, annotation });
            this.hasShownInitialAnnotationToEditDialog = true;
          }
        }
      });
    }
    if (this.props.shouldAutosave && prevProps.sequenceData && prevProps.sequenceData.stateTrackingId && this.props.sequenceData.stateTrackingId !== prevProps.sequenceData.stateTrackingId) {
      this.props.handleSave();
    }
  }
  updateDimensions = debounce(() => {
    this.setState({ randomRerenderTrigger: Math.random() });
  }, 100);
  componentDidMount() {
    if (isMobile()) {
      let firstActivePanelId;
      some(this.getPanelsToShow()[0], (panel) => {
        if (panel.active) {
          firstActivePanelId = panel.id;
          return true;
        }
      });
      this.props.collapseSplitScreen(firstActivePanelId);
    }
    if (this.props.sequenceData?.id) {
      let hasSetSpecificFilter;
      try {
        try {
          const cutsiteFilterDefaultForThisSpecificSeq = JSON.parse(
            localStorage.getItem(
              `tgInitialCutsiteFilter-${this.props.sequenceData?.id}`
            )
          );
          if (cutsiteFilterDefaultForThisSpecificSeq && isArray(cutsiteFilterDefaultForThisSpecificSeq)) {
            hasSetSpecificFilter = true;
            this.props.filteredRestrictionEnzymesUpdate(
              cutsiteFilterDefaultForThisSpecificSeq
            );
          }
        } catch (e) {
        }
        const cutsiteFilterDefaultForAllSeqs = JSON.parse(
          localStorage.getItem(`tgInitialCutsiteFilter`)
        );
        if (!hasSetSpecificFilter && cutsiteFilterDefaultForAllSeqs && isArray(cutsiteFilterDefaultForAllSeqs)) {
          this.props.filteredRestrictionEnzymesUpdate(
            cutsiteFilterDefaultForAllSeqs
          );
        }
      } catch (e) {
      }
    }
    window.addEventListener("resize", this.updateDimensions);
    this.forceUpdate();
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions);
  }
  onTabDragStart = () => {
    this.setState({ tabDragging: true });
  };
  onTabDragEnd = (result) => {
    this.setState({ tabDragging: false });
    const { panelsShownUpdate, panelsShown } = this.props;
    if (!result.destination) {
      return;
    }
    let newPanelsShown;
    if (result.destination.droppableId !== result.source.droppableId) {
      const secondPanel = panelsShown.length === 1 ? [[]] : [];
      const panelsToMapOver = [...panelsShown, ...secondPanel];
      newPanelsShown = map(panelsToMapOver, (panelGroup, groupIndex) => {
        const panelToMove = panelsShown[Number(result.source.droppableId.replace("droppable-id-", ""))][result.source.index];
        if (Number(groupIndex) === Number(result.destination.droppableId.replace("droppable-id-", ""))) {
          return insertItem(
            panelGroup.map((tabPanel) => ({ ...tabPanel, active: false })),
            { ...panelToMove, active: true },
            result.destination.index
          );
        } else if (Number(groupIndex) === Number(result.source.droppableId.replace("droppable-id-", ""))) {
          return removeItem(panelGroup, result.source.index).map(
            (tabPanel, index) => {
              return {
                ...tabPanel,
                ...panelToMove.active && index === 0 && { active: true }
              };
            }
          );
        } else {
          return panelGroup;
        }
      });
    } else {
      newPanelsShown = map(panelsShown, (panelGroup, groupIndex) => {
        if (Number(groupIndex) === Number(result.destination.droppableId.replace("droppable-id-", ""))) {
          return reorder(
            panelGroup.map((tabPanel, i) => {
              return {
                ...tabPanel,
                active: result.source.index === i
              };
            }),
            result.source.index,
            result.destination.index
          );
        }
        return panelGroup;
      });
    }
    filter(newPanelsShown, (panelGroup) => {
      return panelGroup.length;
    });
    panelsShownUpdate(newPanelsShown);
  };
  getPanelsToShow = () => {
    const { panelsShown } = this.props;
    if (isMobile()) return [flatMap(panelsShown)];
    return map(panelsShown);
  };
  onPreviewModeButtonContextMenu = (event) => {
    const { previewModeButtonMenu } = this.props;
    event.preventDefault();
    if (previewModeButtonMenu) {
      ContextMenu.show(previewModeButtonMenu, {
        left: getClientX(event),
        top: getClientY(event)
      });
    }
  };
  closeHotkeyDialog = () => {
    this.setState({ isHotkeyDialogOpen: false });
  };
  openHotkeyDialog = () => {
    this.setState({ isHotkeyDialogOpen: true });
  };
  togglePreviewFullscreen = () => {
    const {
      togglePreviewFullscreen,
      onPreviewModeFullscreenClose,
      previewModeFullscreen: controlledPreviewModeFullscreen
    } = this.props;
    if (togglePreviewFullscreen) togglePreviewFullscreen();
    const previewModeFullscreen = this.state.previewModeFullscreen;
    if (controlledPreviewModeFullscreen === void 0 ? previewModeFullscreen : controlledPreviewModeFullscreen) {
      onPreviewModeFullscreenClose && onPreviewModeFullscreenClose();
    }
    this.setState({
      previewModeFullscreen: !previewModeFullscreen
    });
  };
  render() {
    const { previewModeFullscreen: uncontrolledPreviewModeFullscreen } = this.state;
    const {
      allowPanelTabDraggable = true,
      annotationsToSupport,
      caretPositionUpdate,
      closePanel,
      collapseSplitScreen,
      displayMenuBarAboveTools = true,
      editorName,
      fullScreenOffsets,
      expandTabToSplitScreen,
      extraRightSidePanel,
      getSequenceAtVersion,
      getVersionList,
      handleFullscreenClose,
      height: _height,
      hideSingleImport,
      hideStatusBar,
      hoveredId,
      isFullscreen,
      maxInsertSize,
      getAcceptedInsertChars,
      showAminoAcidUnitAsCodon,
      maxAnnotationsToDisplay,
      minHeight = 400,
      onlyShowLabelsThatDoNotFit = true,
      onSave,
      previewModeButtonMenu,
      previewModeFullscreen: controlledPreviewModeFullscreen,
      readOnly,
      selectionLayer,
      sequenceData = {},
      setPanelAsActive,
      showCircularity,
      showMenuBar,
      StatusBarProps = {},
      style = {},
      togglePanelFullScreen,
      ToolBarProps = {},
      updateSequenceData,
      VersionHistoryViewProps,
      withRotateCircularView = true,
      withZoomCircularView = true,
      withZoomLinearView = true,
      withPreviewMode
    } = this.props;
    if (!this.props.noVersionHistory && this.props.versionHistory && this.props.versionHistory.viewVersionHistory) {
      return /* @__PURE__ */ jsx(
        VersionHistoryView,
        {
          ...{
            onSave,
            //we need to pass this user defined handler
            updateSequenceData,
            caretPositionUpdate,
            editorName,
            sequenceData,
            getVersionList,
            getSequenceAtVersion,
            ...VersionHistoryViewProps
          }
        }
      );
    }
    const previewModeFullscreen = !!((controlledPreviewModeFullscreen === void 0 ? uncontrolledPreviewModeFullscreen : controlledPreviewModeFullscreen) || isFullscreen);
    const editorNode = editorName && typeof CSS !== "undefined" && CSS.escape && document.querySelector(`.${CSS.escape(editorName)}`) || document.querySelector(".veEditor") || document.querySelector(".preview-mode-container");
    let height = Math.max(
      minHeight,
      editorNode && editorNode.parentNode && editorNode.parentNode.clientHeight || 0
    );
    if (_height) height = Math.max(minHeight, _height);
    let editorDimensions = {
      height,
      dimensions: {
        height
      }
    };
    try {
      if (previewModeFullscreen) {
        window.document.body.classList.add("tg-no-scroll-body");
      } else {
        window.document.body.classList.remove("tg-no-scroll-body");
      }
    } catch (e2) {
      console.warn(`Error 3839458:`, e2);
    }
    if (withPreviewMode && !previewModeFullscreen) {
      this.inPreviewMode = true;
      return /* @__PURE__ */ jsx("div", { style: { ...style }, className: "preview-mode-container", children: /* @__PURE__ */ jsxs("div", { style: { position: "relative" }, children: [
        /* @__PURE__ */ jsx("div", { className: "preview-mode-buttons", children: /* @__PURE__ */ jsxs(ButtonGroup, { className: "preview-mode-view-fullscreen", children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              text: "Open Editor",
              intent: Intent.PRIMARY,
              onClick: this.togglePreviewFullscreen
            }
          ),
          previewModeButtonMenu && /* @__PURE__ */ jsx(
            Button,
            {
              icon: "caret-down",
              intent: Intent.PRIMARY,
              onClick: this.onPreviewModeButtonContextMenu
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: { padding: 40 },
            className: "preview-mode-simple-sequence-view",
            children: /* @__PURE__ */ jsx(
              SimpleCircularOrLinearView,
              {
                sequenceData,
                hoveredId,
                tabHeight,
                selectionLayer,
                editorName,
                height: null,
                isProtein: sequenceData.isProtein,
                annotationLabelVisibility: {
                  features: false,
                  parts: false,
                  cutsites: false,
                  primers: false
                }
              }
            )
          }
        )
      ] }) });
    } else {
      this.inPreviewMode = false;
    }
    const tabDraggable = allowPanelTabDraggable && !isMobile();
    const { tabDragging } = this.state;
    let xOffset = 0;
    let yOffset = 0;
    if (fullScreenOffsets && previewModeFullscreen) {
      xOffset = fullScreenOffsets.xOffset || 0;
      yOffset = fullScreenOffsets.yOffset || 0;
    }
    const w = window, d = document, e = d.documentElement, g = d.getElementsByTagName("body")[0], x = w.innerWidth || e.clientWidth || g.clientWidth, y = w.innerHeight || e.clientHeight || g.clientHeight;
    const windowDimensions = {
      width: x - xOffset,
      height: Math.max(y, minHeight) - yOffset
    };
    const reflexElementProps = {
      propagateDimensions: true,
      // resizeHeight: true,
      renderOnResizeRate: 50,
      renderOnResize: true,
      className: "ve-panel"
    };
    const panelsToShow = this.getPanelsToShow();
    this.hasFullscreenPanel = false;
    map(panelsToShow, (panelGroup) => {
      panelGroup.forEach(({ fullScreen }) => {
        if (fullScreen) this.hasFullscreenPanel = true;
      });
    });
    const pickedUserDefinedHandlersAndOpts = pick(
      this.props,
      userDefinedHandlersAndOpts
    );
    const panels = flatMap(panelsToShow, (panelGroup, index) => {
      let activePanelId;
      let activePanelType;
      let isFullScreen;
      let panelPropsToSpread = {};
      panelGroup.forEach((panelProps) => {
        const { type, id, active, fullScreen } = panelProps;
        if (fullScreen) isFullScreen = true;
        if (active) {
          activePanelType = type || id;
          activePanelId = id;
          panelPropsToSpread = panelProps;
        }
      });
      if (this.hasFullscreenPanel && !isFullScreen) {
        return;
      }
      if (isFullScreen) {
        editorDimensions = {
          ...editorDimensions,
          ...windowDimensions,
          dimensions: windowDimensions
        };
      }
      const panelMap = {
        ..._panelMap,
        ...this.props.panelMap
      };
      const Panel = panelMap[activePanelType] && panelMap[activePanelType].comp || panelMap[activePanelType];
      const panelSpecificProps = panelMap[activePanelType] && panelMap[activePanelType].panelSpecificProps;
      const panelSpecificPropsToSpread = panelMap[activePanelType] && panelMap[activePanelType].panelSpecificPropsToSpread;
      const panel = Panel ? /* @__PURE__ */ createElement(
        Panel,
        {
          withZoomLinearView,
          withRotateCircularView,
          withZoomCircularView,
          ...pickedUserDefinedHandlersAndOpts,
          ...panelSpecificProps && pick(this.props, panelSpecificProps),
          ...panelSpecificPropsToSpread && panelSpecificPropsToSpread.reduce((acc, key) => {
            acc = { ...acc, ...get(this.props, key) };
            return acc;
          }, {}),
          circ_rotationRadians: this.state.rotationRadians,
          circ_setRotationRadians: (val) => {
            this.setState({ rotationRadians: val });
          },
          circ_zoomLevel: this.state.zoomLevel,
          circ_setZoomLevel: (val) => {
            this.setState({ zoomLevel: val });
          },
          maxAnnotationsToDisplay,
          key: activePanelId,
          fontHeightMultiplier: this.props.fontHeightMultiplier,
          rightClickOverrides: this.props.rightClickOverrides,
          clickOverrides: this.props.clickOverrides,
          doubleClickOverrides: this.props.doubleClickOverrides,
          ...panelPropsToSpread,
          editorName,
          maxInsertSize,
          getAcceptedInsertChars,
          showAminoAcidUnitAsCodon,
          isProtein: sequenceData.isProtein,
          onlyShowLabelsThatDoNotFit,
          tabHeight,
          nameFontSizeCircularView: this.props.nameFontSizeCircularView,
          ...editorDimensions,
          isInsideEditor: true
        }
      ) : /* @__PURE__ */ jsx("div", { children: " No Panel Found!" });
      const showTabRightClickContextMenu = (e2, id) => {
        const tabIdToUse = id || activePanelId;
        showContextMenu(
          [
            {
              onClick: () => {
                panelsToShow.length > 1 ? collapseSplitScreen(tabIdToUse) : expandTabToSplitScreen(tabIdToUse);
              },
              text: panelsToShow.length > 1 ? "Collapse Split Screen" : "View in Split Screen"
            },
            {
              onClick: () => {
                togglePanelFullScreen(tabIdToUse);
              },
              text: "View in Fullscreen"
            }
          ],
          void 0,
          e2
        );
        e2.preventDefault();
        e2.stopPropagation();
      };
      const toReturn = [];
      if (index > 0) {
        toReturn.push(
          /* @__PURE__ */ jsx(
            ReflexSplitter,
            {
              style: {
                // height: height + 38,
                zIndex: 1
              },
              propagate: true
            },
            activePanelId + "splitter"
          )
        );
      }
      toReturn.push(
        /* @__PURE__ */ jsx(
          ReflexElement,
          {
            activePanelId,
            minSize: "200",
            ...reflexElementProps,
            children: [
              /* @__PURE__ */ jsx(
                Droppable,
                {
                  direction: "horizontal",
                  droppableId: "droppable-id-" + index.toString(),
                  children: (drop_provided, drop_snapshot) => /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: classNames("ve-draggable-tabs", {
                        "is-dragging-over": drop_snapshot.isDraggingOver
                      }),
                      "data-test": "ve-draggable-tabs" + index,
                      ref: drop_provided.innerRef,
                      style: {
                        height: tabHeight,
                        paddingLeft: 3
                      },
                      children: [
                        panelGroup.map(({ id, name, canClose }, index2) => {
                          let nameToShow = name || id;
                          if (isMobile()) {
                            nameToShow = nameToShow.replace("Map", "");
                          }
                          return /* @__PURE__ */ jsx(
                            Draggable,
                            {
                              isDragDisabled: !tabDraggable,
                              index: index2,
                              draggableId: id,
                              children: (provided) => /* @__PURE__ */ jsx(
                                "div",
                                {
                                  style: {
                                    wordWrap: "normal",
                                    flex: "0 0 auto",
                                    maxWidth: "100%",
                                    fontSize: "14px"
                                  },
                                  onClick: () => {
                                    setPanelAsActive(id);
                                  },
                                  children: /* @__PURE__ */ jsx(
                                    "div",
                                    {
                                      onContextMenu: (e2) => {
                                        showTabRightClickContextMenu(e2, id);
                                      },
                                      ref: provided.innerRef,
                                      ...provided.draggableProps,
                                      ...provided.dragHandleProps,
                                      style: {
                                        // some basic styles to make the items look a bit nicer
                                        userSelect: "none",
                                        cursor: tabDraggable ? "move" : "pointer",
                                        flex: "0 0 auto",
                                        ...provided.draggableProps.style
                                      },
                                      children: /* @__PURE__ */ jsxs(
                                        "div",
                                        {
                                          style: {
                                            padding: 3,
                                            borderBottom: id === activePanelId ? "2px solid #106ba3" : "none",
                                            color: id === activePanelId ? "#106ba3" : "undefined",
                                            marginLeft: 13,
                                            marginRight: 13
                                          },
                                          className: (id === activePanelId ? "veTabActive " : "") + camelCase("veTab-" + nameToShow),
                                          children: [
                                            isFullScreen && /* @__PURE__ */ jsx(
                                              "div",
                                              {
                                                style: {
                                                  zIndex: 15002,
                                                  position: "fixed",
                                                  top: 3,
                                                  right: 25
                                                },
                                                children: /* @__PURE__ */ jsx(
                                                  Tooltip,
                                                  {
                                                    position: "left",
                                                    content: "Minimize Tab",
                                                    children: /* @__PURE__ */ jsx(
                                                      Button,
                                                      {
                                                        minimal: true,
                                                        icon: "minimize",
                                                        onClick: (e2) => {
                                                          e2.stopPropagation();
                                                          togglePanelFullScreen(activePanelId);
                                                        }
                                                      }
                                                    )
                                                  }
                                                )
                                              }
                                            ),
                                            nameToShow,
                                            canClose && /* @__PURE__ */ jsx(
                                              Icon,
                                              {
                                                icon: "small-cross",
                                                onClick: () => {
                                                  if (this.props.onPanelTabClose && !this.props.onPanelTabClose(id))
                                                    return;
                                                  closePanel(id);
                                                },
                                                style: { paddingLeft: 5 },
                                                className: "ve-clickable"
                                              }
                                            )
                                          ]
                                        }
                                      )
                                    }
                                  )
                                }
                              )
                            },
                            id
                          );
                        }),
                        drop_provided.placeholder
                      ]
                    }
                  )
                },
                "droppable-id-" + index.toString()
              ),
              ...panelsToShow.length === 1 ? [
                /* @__PURE__ */ jsx(
                  Droppable,
                  {
                    direction: "horizontal",
                    droppableId: "droppable-id-" + (index + 1).toString(),
                    children: (drop_provided, drop_snapshot) => /* @__PURE__ */ jsxs(
                      "div",
                      {
                        ref: drop_provided.innerRef,
                        style: getSplitScreenListStyle(
                          drop_snapshot.isDraggingOver,
                          tabDragging
                        ),
                        children: [
                          /* @__PURE__ */ jsxs(
                            "div",
                            {
                              style: {
                                position: "absolute",
                                left: "45%",
                                top: "45%",
                                fontSize: 26
                              },
                              children: [
                                " ",
                                "+ Add Tab"
                              ]
                            }
                          ),
                          drop_provided.placeholder
                        ]
                      }
                    )
                  },
                  "extra-drop-box"
                )
              ] : [],
              isFullScreen ? /* @__PURE__ */ jsx(
                "div",
                {
                  className: "veWhiteBackground",
                  style: {
                    zIndex: 15e3,
                    position: "fixed",
                    top: 0,
                    left: 0,
                    ...windowDimensions
                  },
                  children: panel
                },
                "veWhiteBackground"
              ) : panel
            ]
          },
          activePanelId
        )
      );
      return toReturn;
    });
    if (extraRightSidePanel) {
      panels.push(
        /* @__PURE__ */ jsx(
          ReflexSplitter,
          {
            style: {
              zIndex: 1
            },
            propagate: true
          },
          "extraRightSidePanelSplitter"
        )
      );
      panels.push(
        /* @__PURE__ */ jsx(
          ReflexElement,
          {
            minSize: "350",
            maxSize: "350",
            ...reflexElementProps,
            children: extraRightSidePanel
          },
          "extraRightSidePanel"
        )
      );
    }
    return /* @__PURE__ */ jsxs(React.Fragment, { children: [
      /* @__PURE__ */ jsxs(
        DropHandler,
        {
          importSequenceFromFile: this.props.importSequenceFromFile,
          disabled: readOnly || hideSingleImport,
          style: {
            width: "100%",
            maxWidth: "100%",
            // ...(fitHeight && {
            // height: "100%",
            //  }),
            position: "relative",
            // height: "100%",
            // ...(fitHeight && {
            height,
            minHeight,
            display: "flex",
            flexDirection: "column",
            ...previewModeFullscreen && {
              background: "white",
              zIndex: 15e3,
              position: "fixed",
              // paddingTop: 20,
              top: yOffset || 0,
              left: xOffset || 0,
              ...windowDimensions
            },
            ...style
          },
          className: `veEditor ${editorName} ${previewModeFullscreen ? "previewModeFullscreen" : ""}`,
          children: [
            /* @__PURE__ */ createElement(
              ToolBar,
              {
                ...pickedUserDefinedHandlersAndOpts,
                openHotkeyDialog: this.openHotkeyDialog,
                key: "toolbar",
                showMenuBar,
                displayMenuBarAboveTools,
                handleFullscreenClose: handleFullscreenClose || this.togglePreviewFullscreen,
                isProtein: sequenceData.isProtein,
                userDefinedHandlersAndOpts,
                annotationsToSupport,
                closeFullscreen: !!(isFullscreen ? handleFullscreenClose : previewModeFullscreen),
                modifyTools: this.props.modifyTools,
                contentLeft: this.props.contentLeft,
                editorName,
                withDigestTool: true,
                beforeReadOnlyChange: this.props.beforeReadOnlyChange,
                ...ToolBarProps
              }
            ),
            /* @__PURE__ */ jsx(
              CommandHotkeyHandler,
              {
                menuSearchHotkey: this.props.menuSearchHotkey,
                hotkeyDialogProps: {
                  isOpen: this.state.isHotkeyDialogOpen,
                  onClose: this.closeHotkeyDialog
                },
                ...pickedUserDefinedHandlersAndOpts,
                editorName
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  position: "relative",
                  flexGrow: "1",
                  minHeight: 0,
                  display: "flex"
                },
                className: "tg-editor-container",
                id: "section-to-print",
                children: /* @__PURE__ */ jsx(
                  DragDropContext,
                  {
                    onDragStart: this.onTabDragStart,
                    onDragEnd: this.onTabDragEnd,
                    children: /* @__PURE__ */ jsx(
                      ReflexContainer,
                      {
                        onPanelCollapse: ({ activePanelId }) => {
                          this.props.collapsePanel(activePanelId);
                        },
                        orientation: "vertical",
                        children: panels
                      }
                    )
                  }
                )
              }
            ),
            !hideStatusBar && /* @__PURE__ */ jsx(
              StatusBar,
              {
                ...pickedUserDefinedHandlersAndOpts,
                isProtein: sequenceData.isProtein,
                showCircularity: !!(showCircularity && !sequenceData.isProtein && !sequenceData.isOligo && !sequenceData.isRna),
                editorName,
                showAminoAcidUnitAsCodon,
                ...StatusBarProps
              }
            )
          ]
        },
        "dropHandler"
      ),
      /* @__PURE__ */ jsx(
        GlobalDialog,
        {
          editorName,
          ...pickedUserDefinedHandlersAndOpts,
          dialogOverrides: pick(this.props, [
            "AddOrEditFeatureDialogOverride",
            "AddOrEditPartDialogOverride",
            "AddOrEditPrimerDialogOverride"
          ])
        }
      ),
      some(sequenceData.features, { isPreview: true }) && /* @__PURE__ */ jsxs(
        "div",
        {
          className: "bp3-card bp3-elevation-2",
          style: {
            position: "fixed",
            bottom: 20,
            right: 20,
            padding: 15,
            zIndex: 1e4
          },
          children: [
            /* @__PURE__ */ jsx("b", { style: { display: "block", marginBottom: 10 }, children: "Preview Auto Annotate" }),
            /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 10 }, children: [
              /* @__PURE__ */ jsx(
                Button,
                {
                  onClick: () => {
                    const previewFeatures = filter(
                      sequenceData.features,
                      (f) => f.isPreview
                    );
                    previewFeatures.forEach((f) => {
                      this.props.deleteFeature(f);
                    });
                  },
                  children: "Close"
                }
              ),
              /* @__PURE__ */ jsx(
                Button,
                {
                  intent: Intent.PRIMARY,
                  onClick: () => {
                    const previewFeatures = filter(
                      sequenceData.features,
                      (f) => f.isPreview
                    );
                    previewFeatures.forEach((f) => {
                      const updatedFeature = {
                        ...f,
                        isPreview: false,
                        opacity: 1,
                        name: f.name.replace(/ \(\d+% match\)$/, "")
                      };
                      this.props.upsertFeature(updatedFeature);
                    });
                  },
                  children: "Apply All"
                }
              )
            ] })
          ]
        }
      )
    ] });
  }
}
Editor.childContextTypes = {
  blueprintPortalClassName: PropTypes.string
};
export default compose(
  connectToEditor(
    ({
      panelsShown,
      annotationsToSupport,
      versionHistory,
      sequenceData = {},
      selectionLayer
    }, { additionalEnzymes }) => {
      return {
        additionalEnzymes: getAdditionalEnzymesSelector(
          null,
          additionalEnzymes
        ),
        annotationsToSupport,
        selectionLayer,
        panelsShown,
        versionHistory,
        sequenceData
      };
    }
  ),
  withHandlers({ handleSave, importSequenceFromFile })
)(Editor);
